// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * SeverityIcon
 *
 * Shared severity icon components and styling configuration used by
 * DashboardAlertsPanel and ActivityLogPage.
 */

// ============================================
// Severity Styling Configuration (Figma)
// ============================================

export interface SeverityStyle {
  variant: 'warning' | 'exclamation' | 'check' | 'info';
  iconColor: string;
  iconBg: string;
}

export const severityStyles: Record<string, SeverityStyle> = {
  URGENT: {
    variant: 'warning',
    iconColor: '#96040E',
    iconBg: '#FEEEEE',
  },
  WARNING: {
    variant: 'exclamation',
    iconColor: '#733F01',
    iconBg: '#F9F6D3',
  },
  INFO: {
    variant: 'check',
    iconColor: '#004E24',
    iconBg: '#EAFAEF',
  },
};

export const insightStyle: SeverityStyle = {
  variant: 'info',
  iconColor: '#193cb8',
  iconBg: '#E5EFFB',
};

import { AppIcon } from '@loa/ui-components';

// ============================================
// Severity glyph (via @loa/ui-components AppIcon registry)
// ============================================
// The actual SVG paths live in `packages/ui-components/src/components/icons/`
// (SeverityWarningIcon, SeverityExclamationIcon, SeverityCheckIcon,
// SeverityInfoIcon). This thin wrapper picks the right glyph for the
// current variant.

const VARIANT_TO_APPICON: Record<SeverityStyle['variant'], string> = {
  warning: 'severity warning',
  exclamation: 'severity exclamation',
  check: 'severity check',
  info: 'severity info',
};

// ============================================
// Time Formatting Helper
// ============================================

export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} mins ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'yesterday';
  return `${diffDays} days ago`;
}

// ============================================
// Severity Icon Component
// ============================================

type SeverityIconProps = Readonly<{
  style: SeverityStyle;
  size?: number;
}>;

export function SeverityIcon({ style, size = 28 }: SeverityIconProps) {
  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '100px',
        backgroundColor: style.iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <AppIcon
        name={VARIANT_TO_APPICON[style.variant]}
        size={16}
        color={style.iconColor}
      />
    </div>
  );
}
